import './AssetPreview.scss';

import { IconSlash } from '@hulu-react-style-components/icons';
import envConfig from '@hulu/env-config';
import { useFormikContext } from 'formik';
import React from 'react';
import ReactPlayer from 'react-player';

import { getAcceptableUrl } from '../_ForCommonLibrary/util/getAcceptableUrl';
import { Asset2ReviewStatus } from '../../apis/graphql';
import { useAsset } from '../../hooks/useAsset';
import type { Asset2 } from '../../models';
import { isExtendedDisplayAsset, isExtendedTextAsset, isExtendedVideoAsset } from '../../utils/assets';
import bem from '../../utils/bem';
import { titleCase } from '../../utils/formatting';
import type { AdFormValues } from '../AdForm/adFormik';
import { useCalculatedCreativeDataByAdCountries } from '../AdForm/CreativeBuilders/hooks/useCalculatedCreativeDataByAdCountries';
import useGetAssetById from '../AdForm/CreativeBuilders/hooks/useGetAssetById';
import useTranscodingFailure from '../AdForm/CreativeBuilders/hooks/useTranscodingFailure';
import AssetDetails from '../AssetDetails';
import Loader from '../Loader';
import Pill from '../Pill';
import AssetRejectionReasonsModal from '../StatusModal/AssetRejectionReasonsModal/AssetRejectionReasonsModal';
import { getColor, getPreviewUrl } from './util';

export interface AssetPreviewProps {
  asset: Partial<Asset2>;
  additionalErrorClassName?: string;
  lineItemPublisherList: string[];
}

const [block, element] = bem('asset-preview');

function AssetPreview({
  asset,
  additionalErrorClassName,
  lineItemPublisherList,
}: Readonly<AssetPreviewProps>): React.JSX.Element {
  const {
    values: { creative, lineItem },
  } = useFormikContext<AdFormValues>();

  const { derivedCreativeReviewStatus, derivedCreativeTranscodingStatus } = useCalculatedCreativeDataByAdCountries(
    creative,
    lineItem
  );

  const { asset: assetDetails, loading } = useGetAssetById(asset.id, lineItemPublisherList);
  const transcodeStatus =
    assetDetails && isExtendedVideoAsset(assetDetails) ? assetDetails?.transcode?.status : undefined;

  const { message, name: transcodingFailureName } = useTranscodingFailure({
    assetId: asset.id,
  });

  const { assetUrl, hasError } = useAsset({ url: getPreviewUrl(asset.id), assetType: asset.subClass });

  const availableReview = asset.review || assetDetails?.review || '';

  const renderPreviewPlaceholder = (
    <div className={element('placeholder')} data-testid="asset-preview-placeholder">
      <IconSlash className={element('placeholder-icon')} aria-label="preview unavailable icon" />
      <span className={element('placeholder-message')}>Preview unavailable</span>
    </div>
  );

  const renderVideoAsset =
    assetDetails && isExtendedVideoAsset(assetDetails) ? (
      <div className={element('video-container')}>
        {hasError ? (
          renderPreviewPlaceholder
        ) : !assetUrl ? (
          <>
            <div className={element('loading-content')}>
              <Loader />
            </div>
            <video controls src={''} aria-label="video-player" />
          </>
        ) : (
          <ReactPlayer url={assetUrl} controls={true} width={`100%`} height={`100%`} />
        )}
      </div>
    ) : null;

  const renderImageAsset = isExtendedDisplayAsset(assetDetails) ? (
    <div className={element('image-container')}>
      {hasError ? (
        renderPreviewPlaceholder
      ) : !assetUrl ? (
        <Loader />
      ) : (
        <img alt={assetDetails?.name} src={assetUrl} className={additionalErrorClassName} />
      )}
    </div>
  ) : null;

  const renderTextAsset = isExtendedTextAsset(assetDetails) ? (
    <div className={element('text-asset-container')}>
      <span className={element('asset-name')}>{asset?.name}</span>
      <textarea value={assetDetails.text} disabled />
    </div>
  ) : null;

  const isAssetRejected = availableReview === Asset2ReviewStatus.Declined;
  const videoDuration = isExtendedVideoAsset(assetDetails)
    ? assetDetails.videoMetadata?.duration ?? undefined
    : undefined;
  const fileSize =
    isExtendedVideoAsset(assetDetails) && assetDetails.videoMetadata?.fileSizeBytes
      ? assetDetails.videoMetadata?.fileSizeBytes
      : 0;

  return (
    <div className={block()}>
      {availableReview && !isExtendedTextAsset(assetDetails) && (
        <Pill
          className={element('review-status-pill')}
          textComponent={
            <div className={element('review-status-content')}>
              <div>{titleCase(availableReview)}</div>
              {assetDetails && isAssetRejected && <AssetRejectionReasonsModal asset={assetDetails} />}
            </div>
          }
          color={getColor(availableReview)}
        />
      )}

      {renderImageAsset}
      {renderVideoAsset}
      {renderTextAsset}

      <AssetDetails
        assetDetailsUrl={getAcceptableUrl(loading, asset.id)}
        assetTagList={assetDetails?.assetTagList ?? []}
        createdAt={assetDetails?.createdAt ? new Date(assetDetails?.createdAt) : new Date()}
        filename={assetDetails?.name}
        fileSize={fileSize}
        fileUrl={`${envConfig.REACT_APP_CAMPAIGN_GRAPHQL_ORIGIN}/api/asset/${asset.id}`}
        review={availableReview}
        transcodeStatus={transcodeStatus}
        transcodingDetails={{ name: transcodingFailureName, message }}
        videoDuration={videoDuration}
        isDisplayAsset={!!assetDetails && isExtendedDisplayAsset(assetDetails)}
        isTextAsset={!!assetDetails && isExtendedTextAsset(assetDetails)}
        approvedPublishers={assetDetails?.approvedPublishers}
        assetId={asset.id}
        creativeId={creative.id}
        derivedCreativeReviewStatus={derivedCreativeReviewStatus}
        derivedCreativeTranscodingStatus={derivedCreativeTranscodingStatus}
      />
    </div>
  );
}

export default AssetPreview;
